import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import MakalahOutlinePage from './MakalahOutlinePage'
import MakalahResultPage from './MakalahResultPage'
import { generateMakalahOutline, countWords } from './makalahUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'

// Makalah Agent States
const AGENT_STATES = {
  OUTLINE: 'outline',
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const MakalahAgent = ({ query, onBack, llmService }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.OUTLINE)
  const [outline, setOutline] = useState([])
  const [generatedContent, setGeneratedContent] = useState({})
  const [academicSources, setAcademicSources] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()

  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingOutline = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `makalah_agent_${query}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedOutline = sessionStorage.getItem(getSessionKey('outline'))
        const persistedContent = sessionStorage.getItem(getSessionKey('content'))
        const persistedSources = sessionStorage.getItem(getSessionKey('sources'))
        
        if (persistedState && persistedOutline) {
          const state = JSON.parse(persistedState)
          const outlineData = JSON.parse(persistedOutline)
          
          console.log('📋 Loading persisted makalah state:', state.currentState)
          setCurrentState(state.currentState)
          setOutline(outlineData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          if (persistedContent) {
            const contentData = JSON.parse(persistedContent)
            setGeneratedContent(contentData)
          }
          
          if (persistedSources) {
            const sourcesData = JSON.parse(persistedSources)
            setAcademicSources(sourcesData)
          }
          
          // Don't generate outline if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('outline'))
          sessionStorage.removeItem(getSessionKey('content'))
          sessionStorage.removeItem(getSessionKey('sources'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip outline generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`makalah_agent_${oldQuery}_state`)
        sessionStorage.removeItem(`makalah_agent_${oldQuery}_outline`)
        sessionStorage.removeItem(`makalah_agent_${oldQuery}_content`)
        sessionStorage.removeItem(`makalah_agent_${oldQuery}_sources`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    // Generate initial outline only if needed
    if (query && !hasInitialized && !isGeneratingOutline.current && !hasEverGenerated.current) {
      generateInitialOutline()
    }
  }, [query]) // Only depend on query, not llmService

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && outline.length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('outline'), JSON.stringify(outline))
        
        if (Object.keys(generatedContent).length > 0) {
          sessionStorage.setItem(getSessionKey('content'), JSON.stringify(generatedContent))
        }
        
        if (academicSources.length > 0) {
          sessionStorage.setItem(getSessionKey('sources'), JSON.stringify(academicSources))
        }
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, outline, generatedContent, academicSources, hasInitialized, query])

  const generateInitialOutline = async () => {
    if (isGeneratingOutline.current || hasEverGenerated.current) {
      console.log('⚠️ Outline generation already in progress or completed, skipping...')
      return
    }
    
    isGeneratingOutline.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      console.log('🔄 Generating initial outline for:', query)
      
      const result = await generateMakalahOutline(query, llmService)
      setOutline(result.outline)
      setAcademicSources(result.academicSources)
      setHasInitialized(true)
      hasEverGenerated.current = true
      
      console.log('✅ Initial outline generated successfully')
    } catch (error) {
      console.error('❌ Error generating initial outline:', error)
      setError('Gagal membuat outline awal')
      setHasInitialized(false)
      hasEverGenerated.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
      isGeneratingOutline.current = false
    }
  }

  const handleStartGeneration = async (finalOutline) => {
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.MAKALAH)
    if (!canProceed) {
      return // Stop execution if insufficient credits
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setOutline(finalOutline)
    setProgress({ current: 0, total: finalOutline.length })
    setIsLoading(true)
    setError(null)
    
    try {
      const content = {}
      
      // Generate content for each section sequentially
      for (let i = 0; i < finalOutline.length; i++) {
        const section = finalOutline[i]
        
        // Update progress
        setProgress(prev => ({ ...prev, current: i + 1 }))
        
        console.log(`🔄 Memproses bagian ${i + 1}/${finalOutline.length}: ${section.title}`)
        
        // Generate content for this section with previous content for continuity
        const sectionContent = await generateSectionContent(section, query, llmService, content, academicSources)
        content[section.id] = sectionContent
        
        // Update generated content
        setGeneratedContent(prevContent => ({ ...prevContent, [section.id]: sectionContent }))
        
        console.log(`✅ Selesai memproses: ${section.title}`)
        
        // Small delay to prevent overwhelming the API
        if (i < finalOutline.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
      
      console.log('🎉 Semua bagian makalah selesai diproses')
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful completion
      try {
        const creditResult = await consumeCredits(AGENT_TYPES.MAKALAH)
        if (creditResult.success) {
          console.log('✅ Kredit berhasil dikonsumsi untuk makalah agent')
        } else {
          console.warn('⚠️ Gagal mengkonsumsi kredit:', creditResult.error)
        }
      } catch (creditError) {
        console.error('❌ Error saat mengkonsumsi kredit:', creditError)
      }
      
      // Save to agent history
      try {
        await AgentHistoryIntegration.saveMakalahResult({
          query: query,
          outline: finalOutline,
          generatedContent: content,
          academicSources: academicSources,
          progress: { current: finalOutline.length, total: finalOutline.length }
        })
        console.log('✅ Hasil makalah berhasil disimpan ke history')
      } catch (historyError) {
        console.error('❌ Error menyimpan ke history:', historyError)
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartGeneration:', err)
      setError(err.message)
      setCurrentState(AGENT_STATES.OUTLINE)
    } finally {
      setIsLoading(false)
    }
  }

  const generateSectionContent = async (section, originalQuery, llmService, previousContent = {}, existingAcademicSources = []) => {
    // Handle references section specially - don't generate content via LLM
    if (section.id === 'daftar-pustaka' || section.title.toLowerCase().includes('daftar pustaka') || section.title.toLowerCase().includes('referensi')) {
      if (existingAcademicSources && existingAcademicSources.length > 0) {
        const referencesContent = existingAcademicSources.slice(0, 15).map((source, index) => {
          const year = new Date().getFullYear()
          return `${index + 1}. ${source.title} (${year}). Retrieved from ${source.url}`
        }).join('\n\n')
        return `## ${section.title}\n\n${referencesContent}`
      } else {
        return `## ${section.title}\n\nTidak ada referensi yang tersedia.`
      }
    }
    
    // Gunakan sumber yang sudah ada dari outline generation
    let sourcesText = ''
    
    if (existingAcademicSources && existingAcademicSources.length > 0) {
      // Filter sumber yang relevan dengan section ini
      const relevantSources = existingAcademicSources.filter(source => {
        const sectionKeywords = section.title.toLowerCase().split(' ')
        const sourceText = `${source.title} ${source.snippet}`.toLowerCase()
        return sectionKeywords.some(keyword => 
          keyword.length > 3 && sourceText.includes(keyword)
        )
      })
      
      // Jika tidak ada sumber yang relevan, gunakan semua sumber yang ada
      // Gunakan lebih banyak sumber untuk sitasi yang lebih kaya
      const maxSources = Math.min(12, existingAcademicSources.length) // Gunakan maksimal 12 sumber per bagian untuk makalah
      const sourcesToUse = relevantSources.length > 0 ? relevantSources.slice(0, maxSources) : existingAcademicSources.slice(0, maxSources)
      
      if (sourcesToUse.length > 0) {
        sourcesText = sourcesToUse.map((source, index) => 
          `[${index + 1}] ${source.title}\n${source.snippet}\nURL: ${source.url}\n`
        ).join('\n')
        console.log(`✅ Menggunakan ${sourcesToUse.length} sumber untuk: ${section.title}`)
      }
    } else {
      console.log('⚠️ Tidak ada sumber tersedia, menggunakan pengetahuan umum')
    }
    
    // Buat konteks dari SEMUA bagian sebelumnya untuk kontinuitas penuh
    let previousContext = '';
    const previousSections = Object.keys(previousContent);
    if (previousSections.length > 0) {
      previousContext = `\n\nKONTEKS LENGKAP DARI SEMUA BAGIAN SEBELUMNYA (untuk kontinuitas dan kohesi):\n`;
      previousSections.forEach(sectionId => {
        const content = previousContent[sectionId];
        if (content) {
          // Sertakan SELURUH konten dari bagian sebelumnya
          previousContext += `\n=== BAGIAN: ${sectionId.toUpperCase()} ===\n${content}\n\n`;
        }
      });
      previousContext += `\n=== INSTRUKSI KONTINUITAS ===\nPASTIKAN bagian "${section.title}" yang akan Anda tulis:\n- TERHUBUNG secara logis dengan semua bagian sebelumnya\n- MENGGUNAKAN transisi yang tepat dari bagian terakhir\n- MERUJUK kembali konsep, teori, atau temuan yang telah dibahas sebelumnya\n- MEMBANGUN argumen akademik yang berkelanjutan dari fondasi yang sudah diletakkan\n- MENGHINDARI pengulangan yang tidak perlu\n- MENJAGA alur logis dan kohesi keseluruhan makalah\n`;
    }
    
    // Buat prompt untuk menghasilkan KONTEN LENGKAP dalam format Markdown untuk makalah akademik
    const basePrompt = `Tulis KONTEN LENGKAP untuk bagian "${section.title}" dalam makalah akademik dengan topik "${originalQuery}". JANGAN SERTAKAN KALIMAT BASA-BASI DI AWAL OUTPUT.`;
    
    const guidelines = `\nPedoman WAJIB untuk Makalah Akademik:
    - LANGSUNG tulis konten lengkap dan komprehensif tanpa kalimat pembuka atau basa-basi
    - MULAI LANGSUNG dengan heading utama bagian (contoh: ## ${section.title})
    - TARGET KATA: ${section.minWords} kata - PASTIKAN SESUAI TARGET!
    - MAKALAH MAKSIMAL TOTAL 15000 KATA - sesuai standar akademik!
    - Untuk mencapai target kata yang tepat:
      * Tulis setiap sub-bagian dengan detail akademik yang mendalam
      * Berikan analisis kritis dan argumentasi yang kuat
      * Sertakan teori-teori yang relevan dan terkini
      * Tambahkan penjelasan metodologis yang komprehensif
      * Gunakan paragraf yang substantif (150-200 kata per paragraf)
      * Berikan analisis data dan temuan yang detail
      * Sertakan diskusi teoritis dan praktis
    - Gunakan format Markdown dengan heading, subheading, dan formatting yang tepat
    - Untuk list dan nested list, gunakan format yang benar:
      * Gunakan - untuk list utama
      * Gunakan spasi + - untuk nested list (contoh: "  - sub item")
      * Pastikan konsistensi indentasi
    - WAJIB: Semua tabel HARUS dibuat dalam format Markdown yang benar dengan | dan header yang jelas
    - Contoh format tabel yang benar:
      | Header 1 | Header 2 | Header 3 |
      |----------|----------|----------|
      | Data 1   | Data 2   | Data 3   |
    - Tulis dengan gaya akademik yang formal dan objektif
    - Berikan analisis yang mendalam dan argumentasi yang logis
    - Gunakan paragraf yang koheren dan terstruktur dengan baik
    - PENTING: Pastikan bagian ini terhubung logis dengan bagian sebelumnya
    - Gunakan transisi yang tepat dan rujuk konsep yang relevan dari bagian sebelumnya
    - WAJIB: Jika ada data, perbandingan, atau informasi yang dapat disajikan dalam bentuk tabel, maka HARUS dibuat dalam format tabel Markdown
    - Perluas setiap sub-bagian dengan analisis akademik yang mendalam
    - Tambahkan perspektif teoritis dan metodologis yang relevan
    - Gunakan bahasa akademik yang formal namun tetap mudah dipahami
    - Sertakan sitasi yang tepat untuk mendukung argumen
    ${section.description ? `- Deskripsi bagian: ${section.description}` : ''}
    ${section.subsections && section.subsections.length > 0 ? `- Sub-bagian yang harus dicakup: ${section.subsections.join(', ')}` : ''}`;
    
    let prompt;
    if (sourcesText) {
      prompt = `${basePrompt}${previousContext}

SUMBER REFERENSI AKADEMIK YANG HARUS DIGUNAKAN:
${sourcesText}

PASTIKAN untuk:
- Mengintegrasikan informasi dari sumber-sumber akademik di atas ke dalam konten
- Memberikan sitasi dalam format [1], [2], dst. sesuai urutan sumber
- Menggunakan informasi dari sumber untuk memperkuat argumen akademik dan memberikan kredibilitas
- Tidak hanya copy-paste, tapi menganalisis dan mengolah informasi menjadi konten akademik yang koheren
- Menggunakan pendekatan kritis dalam menganalisis sumber-sumber tersebut

${guidelines}

Tulis konten akademik sekarang:`;
    } else {
      prompt = `${basePrompt}${previousContext}

Karena tidak ada sumber khusus yang tersedia, gunakan pengetahuan akademik yang akurat dan terpercaya untuk menulis konten.

${guidelines}

Tulis konten akademik sekarang:`;
    }
    
    try {
      // Use GENIUS mode for high-quality academic content generation
      const response = await llmService.sendMessage(
        [{ role: 'user', content: prompt }],
        { 
          mode: MODEL_MODES.GENIUS,
          maxTokens: 6000, // Increased for longer academic content
          searchType: 'academic',
          stream: false,
          consumeCredits: false // Don't consume credits for content generation
        }
      )
      
      if (!response || response.trim().length === 0) {
        throw new Error(`Konten kosong untuk bagian: ${section.title}`)
      }
      
      const wordCount = countWords(response)
      console.log(`📝 Bagian "${section.title}" selesai: ${wordCount} kata`)
      
      if (wordCount < section.minWords * 0.8) { // Allow 20% tolerance
        console.warn(`⚠️ Bagian "${section.title}" kurang dari target (${wordCount}/${section.minWords} kata)`)
      }
      
      return response
    } catch (error) {
      console.error(`❌ Error generating content for section "${section.title}":`, error)
      throw new Error(`Gagal membuat konten untuk bagian: ${section.title}. ${error.message}`)
    }
  }

  const handleBackToSearch = () => {
    // Clear session storage when going back to search
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('outline'))
      sessionStorage.removeItem(getSessionKey('content'))
      sessionStorage.removeItem(getSessionKey('sources'))
    } catch (error) {
      console.warn('⚠️ Failed to clear session storage:', error)
    }
    
    // Reset state and go back to search
    setCurrentState(AGENT_STATES.OUTLINE)
    setOutline([])
    setGeneratedContent({})
    setAcademicSources([])
    setProgress({ current: 0, total: 0 })
    setError(null)
    setHasInitialized(false)
    hasEverGenerated.current = false
    isGeneratingOutline.current = false
    onBack()
  }

  // Render based on current state
  switch (currentState) {
    case AGENT_STATES.OUTLINE:
      return (
        <MakalahOutlinePage
          query={query}
          outline={outline}
          onOutlineChange={setOutline}
          onStartGeneration={handleStartGeneration}
          onBack={onBack}
          llmService={llmService}
          isLoading={isLoading}
        />
      )
    
    case AGENT_STATES.GENERATING:
    case AGENT_STATES.COMPLETED:
      return (
        <MakalahResultPage
          query={query}
          outline={outline}
          generatedContent={generatedContent}
          academicSources={academicSources}
          progress={progress}
          isLoading={isLoading}
          error={error}
          isCompleted={currentState === AGENT_STATES.COMPLETED}
          onBack={() => setCurrentState(AGENT_STATES.OUTLINE)}
          onBackToSearch={handleBackToSearch}
        />
      )
    
    default:
      return null
  }

  return (
    <>
      {(() => {
        switch (currentState) {
          case AGENT_STATES.OUTLINE:
            return (
              <MakalahOutlinePage
                query={query}
                outline={outline}
                onOutlineChange={setOutline}
                onStartGeneration={handleStartGeneration}
                onBack={onBack}
                llmService={llmService}
                isLoading={isLoading}
              />
            )
          
          case AGENT_STATES.GENERATING:
          case AGENT_STATES.COMPLETED:
            return (
              <MakalahResultPage
                query={query}
                outline={outline}
                generatedContent={generatedContent}
                academicSources={academicSources}
                progress={progress}
                isLoading={isLoading}
                error={error}
                isCompleted={currentState === AGENT_STATES.COMPLETED}
                onBack={() => setCurrentState(AGENT_STATES.OUTLINE)}
                onBackToSearch={handleBackToSearch}
              />
            )
          
          default:
            return null
        }
      })()} 
      <CreditWarningModal 
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        message={warningModal.message}
      />
    </>
  )
}

MakalahAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default MakalahAgent